import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SkeletonText } from '../components';
import { twMerge } from 'tailwind-merge';
import { Button, IconButton } from './ui';

export interface SelectOption {
  id: number;
  label: string;
  subLabel?: string;
}

interface SearchableMultiSelectProps {
  options: SelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  disabled?: boolean;
  maxDisplayed?: number;
}

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  options, selectedIds, onChange, placeholder = 'Select items...', searchPlaceholder = 'Search...',
  emptyMessage = 'No items found', loading = false, disabled = false, maxDisplayed = 3,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownPosition(spaceBelow < 500 && rect.top > spaceBelow ? 'above' : 'below');
    }
  }, [isOpen]);

  useEffect(() => { if (isOpen && searchInputRef.current) searchInputRef.current.focus(); }, [isOpen]);

  const filteredOptions = options.filter(o => {
    const s = searchTerm.toLowerCase();
    return o.label.toLowerCase().includes(s) || (o.subLabel && o.subLabel.toLowerCase().includes(s));
  });

  const toggleSelection = useCallback((id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter(s => s !== id) : [...selectedIds, id]);
  }, [selectedIds, onChange]);

  const selectAll = useCallback(() => {
    onChange([...new Set([...selectedIds, ...filteredOptions.map(o => o.id)])]);
  }, [filteredOptions, selectedIds, onChange]);

  const clearAll = useCallback(() => onChange([]), [onChange]);

  const selectedOptions = options.filter(o => selectedIds.includes(o.id));
  const removeSelection = (id: number, e: React.MouseEvent) => { e.stopPropagation(); onChange(selectedIds.filter(s => s !== id)); };

  return (
    <div className={twMerge('relative w-full', disabled && 'opacity-60 pointer-events-none')} ref={containerRef}>
      <div
        className={twMerge(
          'flex items-center justify-between min-h-[42px] py-2 px-3 bg-bg-secondary border border-border-medium rounded-lg cursor-pointer transition-all',
          'hover:border-accent',
          isOpen && 'border-accent ring-2 ring-accent/20',
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-text-tertiary text-sm">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5 flex-1">
            {selectedOptions.slice(0, maxDisplayed).map(o => (
              <span key={o.id} className="inline-flex items-center gap-1 py-0.5 px-2 bg-aegean/10 text-accent text-xs font-medium rounded-full">
                {o.label}
                <IconButton
                  type="button"
                  icon={<>{'×'}</>}
                  aria-label={`Remove ${o.label}`}
                  variant="danger"
                  size="sm"
                  onClick={e => removeSelection(o.id, e)}
                  className="ml-0.5"
                />
              </span>
            ))}
            {selectedOptions.length > maxDisplayed && (
              <span className="inline-flex items-center py-0.5 px-2 bg-bg-tertiary text-text-secondary text-xs rounded-full">
                +{selectedOptions.length - maxDisplayed} more
              </span>
            )}
          </div>
        )}
        <span className="text-[10px] text-text-secondary ml-2 shrink-0">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className={twMerge(
          'absolute left-0 right-0 z-50 bg-bg-secondary border border-border-light rounded-lg shadow-lg overflow-hidden',
          dropdownPosition === 'above' ? 'bottom-full mb-1' : 'top-full mt-1',
        )}>
          <div className="relative p-2 border-b border-border-light">
            <input ref={searchInputRef} type="text" placeholder={searchPlaceholder} value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)} onClick={e => e.stopPropagation()}
              className="w-full py-2 px-3 border border-border-light rounded-lg text-sm bg-bg-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent" />
            {searchTerm && (
              <IconButton
                type="button"
                icon={<>{'×'}</>}
                aria-label="Clear search"
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              />
            )}
          </div>
          <div className="flex gap-2 px-3 py-2 border-b border-border-light">
            <Button type="button" variant="text" size="sm" onClick={selectAll} disabled={filteredOptions.length === 0}
              className="text-xs hover:underline">Select All ({filteredOptions.length})</Button>
            <Button type="button" variant="text" size="sm" onClick={clearAll} disabled={selectedIds.length === 0}
              className="text-xs hover:underline">Clear All</Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="p-4"><SkeletonText lines={2} /></div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-4 text-sm text-text-tertiary text-center">{emptyMessage}</div>
            ) : (
              filteredOptions.map(option => (
                <div key={option.id}
                  className={twMerge('flex items-center gap-2.5 py-2 px-3 cursor-pointer transition-colors hover:bg-bg-tertiary', selectedIds.includes(option.id) && 'bg-aegean/5')}>
                  <input type="checkbox" checked={selectedIds.includes(option.id)} onChange={() => toggleSelection(option.id)}
                    onClick={e => e.stopPropagation()} className="w-4 h-4 accent-accent cursor-pointer shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{option.label}</div>
                    {option.subLabel && <div className="text-xs text-text-tertiary truncate">{option.subLabel}</div>}
                  </div>
                </div>
              ))
            )}
          </div>
          {selectedIds.length > 0 && (
            <div className="px-3 py-2 border-t border-border-light text-xs text-text-secondary font-medium">{selectedIds.length} selected</div>
          )}
        </div>
      )}
    </div>
  );
};
